import * as z from 'zod';
import {campaignRefSchema, readCampaignRefSchema} from '../campaigns';
import {leadRefSchema, readLeadSchema} from '../leads';
import {readBlockVariantSchema} from "../block-variants";
import {readTrackedEventSchema, timeSeriesSchema} from "../common";
import {readJobSchema} from "../jobs";
import {readAdsCampaignSchema} from "../crm-integrations";

// Enums
export const pageStatus = z.enum(['draft', 'published', 'unpublished', 'waiting', 'queued', 'generating', 'ready', 'error', 'excluded', 'excluded_conflict']);
export type PageStatus = z.infer<typeof pageStatus>;

/**
 * Whether a generated page is stale relative to its inputs / live version:
 * - none: never generated, or in sync
 * - needs_regeneration: a generation input changed (e.g. campaign edited) so the page must be
 *   regenerated; applies whether or not the page is published
 * - needs_republication: published page whose content changed (direct edit or completed
 *   regeneration) and whose live version is now stale
 */
export const pageOutdatedStatus = z.enum(['none', 'needs_regeneration', 'needs_republication']);
export type PageOutdatedStatus = z.infer<typeof pageOutdatedStatus>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readPageRefSchema = z.object({
  id: z.uuid(),
});
export type ReadPageRef = z.infer<typeof readPageRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const pageRefSchema = z.object({
  id: z.uuid(),
});
export type PageRef = z.infer<typeof pageRefSchema>;

// Page statistics schema — mirrors backend PageStatisticsDto.
export const pageStatisticsSchema = z.object({
  blockVariantsCount: z.number().nullable(),
  visitsCount: z.number().nullable(),
  periodVisitsVariationInPercent: z.number().nullable(),
  sessionAverageTimeInSeconds: z.number().nullable(),
  periodSessionAverageTimeVariationInPercent: z.number().nullable(),
  ctaClickPerSession: z.number().nullable(),
  periodCtaClickPerSessionVariationInPercent: z.number().nullable(),
  visitsTimeSeries: timeSeriesSchema.nullable(),
  trackedEvents: z.array(readTrackedEventSchema).nullable(),
});
export type PageStatistics = z.infer<typeof pageStatisticsSchema>;

// 3. Read schema - all properties nullable (API response)
export const readPageSchema = z.object({
  id: z.uuid().nullable(),
  status: pageStatus.nullable(),
  outdatedStatus: pageOutdatedStatus.nullable(),
  // Null on ad-campaign pages (targetType 'ad_campaign').
  lead: readLeadSchema.nullable(),
  // Whether this variant targets a lead (company) or a Google Ads campaign.
  targetType: z.enum(['lead', 'ad_campaign']).nullable(),
  adCampaign: z.lazy(() => readAdsCampaignSchema).nullable(),
  campaign: z.lazy(() => readCampaignRefSchema).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  lastPublishedAt: z.iso.datetime().nullable(),
  // Mirrors EmbeddedPage — feeds getEffectiveStatus (live job state overrides
  // the stored status in list displays).
  lastGenerationJob: z.lazy(() => readJobSchema).nullable(),
  blockVariants: z.array(z.lazy(() => readBlockVariantSchema)).nullable(),
  staticUrl: z.string().nullable(),
  statistics: pageStatisticsSchema.nullable(),
});
export type ReadPage = z.infer<typeof readPageSchema>;

// 4. Create schema - based on DTO serialization groups
export const createPageSchema = z.object({
  lead: leadRefSchema,
  campaign: z.lazy(() => campaignRefSchema),
});
export type CreatePage = z.infer<typeof createPageSchema>;

// 5. Update schema - based on DTO serialization groups (usually partial)
export const updatePageSchema = createPageSchema.partial();
export type UpdatePage = z.infer<typeof updatePageSchema>;

// Legacy export for backward compatibility
export const pageSchema = readPageSchema;
export type Page = ReadPage;
